import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { saveAs } from "file-saver";
import "../PlateBlurTool/PlateBlurTool.css";
import "./VideoMerge.css";

type Job = {
  job_id: string;
  status: "queued" | "processing" | "done" | "error";
  message?: string;
  percent?: number;
};
const base = "/video-api";
export default function VideoMerge() {
  const [files, setFiles] = useState<(File | null)[]>([null, null]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [job, setJob] = useState<Job | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [pollError, setPollError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const controller = useRef<AbortController>();
  const active =
    uploading || job?.status === "queued" || job?.status === "processing";
  useEffect(() => {
    const urls = files.map((file) => (file ? URL.createObjectURL(file) : ""));
    setPreviews(urls);
    return () => urls.forEach((url) => url && URL.revokeObjectURL(url));
  }, [files]);
  useEffect(() => () => controller.current?.abort(), []);
  const jobId = job?.job_id;
  const terminal = job?.status === "done" || job?.status === "error";
  useEffect(() => {
    if (!jobId || terminal) return;
    const abort = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const { data } = await axios.get<Job>(`${base}/status/${jobId}`, {
          signal: abort.signal,
          timeout: 15000,
        });
        setJob(data);
        setPollError(false);
        if (data.status !== "done" && data.status !== "error")
          timer = setTimeout(poll, 2000);
      } catch (err) {
        if (axios.isCancel(err)) return;
        if (axios.isAxiosError(err) && err.response?.status === 404)
          setJob({
            job_id: jobId!,
            status: "error",
            message:
              "This job expired or the service restarted. Please upload your clips again.",
          });
        else setPollError(true);
      }
    }
    timer = setTimeout(poll, 700);
    return () => {
      abort.abort();
      clearTimeout(timer);
    };
  }, [jobId, terminal, retry]);
  function choose(index: number, file?: File) {
    if (!file || active) return;
    if (
      !/\.(mp4|mov|m4v|mkv|webm|avi)$/i.test(file.name) ||
      !file.size ||
      file.size > 50 * 1024 * 1024
    ) {
      setError(
        "Choose a non-empty MP4, MOV, M4V, MKV, WebM or AVI file, up to 50 MB.",
      );
      return;
    }
    setFiles((current) =>
      current.map((value, i) => (i === index ? file : value)),
    );
    setJob(null);
    setError("");
  }
  async function start() {
    if (!files[0] || !files[1] || active) return;
    const abort = new AbortController();
    controller.current = abort;
    setUploading(true);
    setProgress(0);
    setError("");
    setJob(null);
    setPollError(false);
    const body = new FormData();
    files.forEach((file) => body.append("files", file!));
    try {
      const { data } = await axios.post<{ job_id: string }>(
        `${base}/merge`,
        body,
        {
          signal: abort.signal,
          timeout: 300000,
          onUploadProgress: (event) =>
            setProgress(
              event.total ? Math.round((event.loaded * 100) / event.total) : 0,
            ),
        },
      );
      if (!/^[a-f0-9]{32}$/.test(data.job_id)) throw new Error("Invalid job");
      setJob({
        job_id: data.job_id,
        status: "queued",
        message: "Waiting for the video worker",
      });
    } catch (err) {
      if (!axios.isCancel(err))
        setError(
          axios.isAxiosError(err)
            ? err.response?.data?.message ||
                "Upload failed. Check your connection and try again."
            : "Could not start the merge. Please try again.",
        );
    } finally {
      if (!abort.signal.aborted) setUploading(false);
    }
  }
  async function download() {
    if (!job || downloading) return;
    setDownloading(true);
    setError("");
    const abort = new AbortController();
    controller.current = abort;
    try {
      const { data } = await axios.get(`${base}/download/${job.job_id}`, {
        responseType: "blob",
        signal: abort.signal,
        timeout: 120000,
      });
      saveAs(data, "merged-video.mp4");
    } catch (err) {
      if (!axios.isCancel(err))
        setError(
          "Download failed or expired. Try again, or merge your clips again.",
        );
    } finally {
      if (!abort.signal.aborted) setDownloading(false);
    }
  }
  function reset() {
    setFiles([null, null]);
    setJob(null);
    setError("");
    setPollError(false);
    setResetKey((n) => n + 1);
  }
  return (
    <div className="ed-wrap ed-page plate-page merge-page">
      <Helmet>
        <title>Merge Two Videos — Nuhman Tools</title>
        <meta
          name="description"
          content="Join two short video clips into one MP4. Choose the order, preserve audio, and download your merged video."
        />
      </Helmet>
      <nav className="plate-breadcrumb" aria-label="Breadcrumb">
        <Link to="/smart-tools">Tools & utilities</Link>
        <span>/</span>
        <span>Video Merger</span>
      </nav>
      <header className="plate-heading">
        <p className="ed-eyebrow">
          <span className="ed-dot" /> The toolkit · Video editing
        </p>
        <h1>
          Two clips. <em>One story.</em>
        </h1>
        <p>
          Put two short videos together, in the order you choose.
          <br />
          Keep the sound. Download one ready-to-share MP4.
        </p>
      </header>
      <div className="plate-layout">
        <section className="plate-workspace" aria-labelledby="merge-title">
          <div className="plate-panel-heading">
            <span className="ed-eyebrow">01 + 02 / Your clips</span>
            <span>Up to 50 MB & 60 seconds each</span>
          </div>
          <h2 id="merge-title">Bring your moments together.</h2>
          <p className="plate-helper">
            The first clip plays, then the second. No overlap or transition.
          </p>
          <p id="merge-file-formats" className="plate-helper">Supported: MP4, MOV, M4V, MKV, WebM and AVI. Choose a file from your device.</p>
          <div className="merge-clips">
            {files.map((file, index) => (
              <div className="merge-clip" key={index}>
                <label htmlFor={`merge-file-${index}`}>
                  <span className="ed-eyebrow">
                    {index === 0 ? "01 / Plays first" : "02 / Plays next"}
                  </span>
                  <strong>
                    {file?.name ||
                      `Choose ${index === 0 ? "first" : "second"} video`}
                  </strong>
                </label>
                {previews[index] ? (
                  <video
                    controls
                    preload="metadata"
                    src={previews[index]}
                    aria-label={`Preview clip ${index + 1}`}
                  />
                ) : (
                  <div className="merge-placeholder" aria-hidden="true">
                    {index + 1}
                  </div>
                )}
                <input
                  key={`${index}-${resetKey}`}
                  className="plate-file-input"
                  id={`merge-file-${index}`}
                  type="file"
                  aria-describedby="merge-file-formats"
                  disabled={!!active || downloading}
                  onChange={(event) => choose(index, event.target.files?.[0])}
                />
                <button
                  className="plate-secondary"
                  disabled={!!active || downloading}
                  onClick={() =>
                    document.getElementById(`merge-file-${index}`)?.click()
                  }
                >
                  {file ? "Change clip" : "Choose clip"} ↗
                </button>
                {file && (
                  <small>
                    {file.size < 1024 * 1024
                      ? `${Math.ceil(file.size / 1024)} KB`
                      : `${(file.size / 1024 / 1024).toFixed(1)} MB`}
                  </small>
                )}
              </div>
            ))}
          </div>
          <div className="plate-upload-actions">
            <button
              className="plate-secondary"
              disabled={!files[0] || !files[1] || !!active || downloading}
              onClick={() => {
                setFiles(([a, b]) => [b, a]);
                setJob(null);
                setResetKey((n) => n + 1);
              }}
            >
              ⇄ Swap order
            </button>
            <button
              className="ed-button"
              disabled={!files[0] || !files[1] || !!active || downloading}
              onClick={start}
            >
              {uploading ? `Uploading ${progress}%` : "Merge videos ↗"}
            </button>
          </div>
          {error && (
            <p className="plate-error" role="alert">
              {error}
            </p>
          )}
          {(uploading || job) && (
            <section className="plate-job" aria-label="Merge progress">
              <div className="plate-job-heading">
                <h3>
                  {uploading
                    ? "Uploading your clips…"
                    : job?.status === "done"
                      ? "Your video is ready."
                      : job?.status === "error"
                        ? "We couldn’t merge these clips."
                        : "Making one video."}
                </h3>
                <span>{job?.status === "done" ? "✓" : "◷"}</span>
              </div>
              <p role="status">
                {uploading
                  ? "Keep this page open while the clips upload."
                  : job?.message}
              </p>
              {active && (
                <div
                  className="plate-progress"
                  role="progressbar"
                  aria-label="Merge stage"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={uploading ? progress : job?.percent || 0}
                >
                  <span
                    style={{
                      width: `${uploading ? progress : job?.percent || 3}%`,
                    }}
                  />
                </div>
              )}
              {pollError && (
                <div role="alert">
                  <p>
                    We lost the status connection. Your merge may still be
                    running.
                  </p>
                  <button
                    className="plate-secondary"
                    onClick={() => {
                      setPollError(false);
                      setRetry((n) => n + 1);
                    }}
                  >
                    Check status again
                  </button>
                </div>
              )}
              {job?.status === "done" && (
                <div className="plate-result-actions">
                  <button
                    className="ed-button"
                    disabled={downloading}
                    onClick={download}
                  >
                    {downloading ? "Downloading…" : "Download merged video ↓"}
                  </button>
                  <button
                    className="plate-secondary"
                    disabled={downloading}
                    onClick={reset}
                  >
                    Merge another pair
                  </button>
                </div>
              )}
            </section>
          )}
          <p className="plate-retention">
            Clips are uploaded for processing and deleted when the job finishes.
            Download your result within 10 minutes; expired files are cleared
            automatically. Keep this page open to follow your job.
          </p>
        </section>
        <aside className="plate-aside">
          <section className="plate-example merge-example">
            <p className="ed-eyebrow">Better together</p>
            <div className="merge-sequence">
              <span>01</span>
              <b>+</b>
              <span>02</span>
            </div>
            <div className="merge-result">
              01 <span>→</span> 02 <small>ONE MP4</small>
            </div>
            <p>A clean cut from your first clip to your second.</p>
          </section>
          <section className="plate-how">
            <p className="ed-eyebrow">Good to know</p>
            <ol>
              <li>
                <span>01</span>
                <div>
                  <h3>Your order, your story</h3>
                  <p>Preview both clips, then swap them if you like.</p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <h3>Sound stays with each clip</h3>
                  <p>Original audio is preserved. Silent clips stay silent.</p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <h3>Different sizes? All good.</h3>
                  <p>
                    The first clip sets the canvas. Other sizes fit with black
                    borders, without cropping or stretching. Output is up to
                    720p at 30 fps.
                  </p>
                </div>
              </li>
            </ol>
          </section>
        </aside>
      </div>
      <Link className="plate-back" to="/smart-tools">
        ← Explore all tools
      </Link>
    </div>
  );
}
